import { useTranslation } from "react-i18next";
import { format } from "date-fns/format";
import { getDateFnsLocale } from "@tellescope/utils";
import { Button, Card, CardContent } from "@tellescope/ui";
import { ChevronLeft, Scale, Droplet, Utensils } from "lucide-react";
import type { EnduserObservation } from "@tellescope/types-models";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { useTrackerHistory } from "./useTrackerHistory";
import { EntryRow } from "./EntryRow";
import { sumMacro, type MetricId } from "./metrics";
import { lbToDisplayWeight, mlToOz } from "../../../utils/nutritionUnitConversions";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

interface TrackerHistoryProps {
  state: TrackerState;
}

const FoodRow = ({
  entry,
  onSelectMacro,
}: {
  entry: EnduserObservation;
  onSelectMacro: (metricId: MetricId) => void;
}) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);
  const macros: { metricId: MetricId; value: number; label: string }[] = [
    { metricId: "calories", value: sumMacro([entry]), label: t("cal") },
    { metricId: "protein", value: sumMacro([entry], "Protein"), label: t("P") },
    { metricId: "carbs", value: sumMacro([entry], "Carbohydrate"), label: t("C") },
    { metricId: "fat", value: sumMacro([entry], "Fat"), label: t("F") },
  ];

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted text-muted-foreground shrink-0">
          <Utensils className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            {entry.components?.find((c) => c.code.coding)?.code.text ?? t("Meal")}
          </p>
          <p className="text-xs text-muted-foreground">{format(new Date(entry.timestamp), "h:mm a", { locale })}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {macros.map((macro) => (
          <button
            key={macro.metricId}
            type="button"
            onClick={() => onSelectMacro(macro.metricId)}
            className="text-xs px-1.5 py-1 rounded hover:bg-muted tabular-nums"
          >
            {Math.round(macro.value)}
            {macro.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const TrackerHistory = ({ state }: TrackerHistoryProps) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);
  const { groupedByDay, hasAnyEntries } = useTrackerHistory(state);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={state.goToDashboard} className="-ml-2">
        <ChevronLeft className="h-4 w-4 mr-1" />
        {t("Back")}
      </Button>

      {!hasAnyEntries ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            {t("No entries yet. Your logged weigh-ins and meals will appear here.")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedByDay.map((group) => (
            <div key={group.dayKey}>
              <p className="px-1 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.dayLabel}
              </p>
              <div className="divide-y rounded-lg border">
                {group.entries.map((entry) =>
                  entry.type === "food" ? (
                    <FoodRow key={entry.id} entry={entry} onSelectMacro={state.goToMetricDetail} />
                  ) : (
                    <EntryRow
                      key={entry.id}
                      icon={entry.type === "weight" ? <Scale className="h-4 w-4" /> : <Droplet className="h-4 w-4" />}
                      title={entry.type === "weight" ? t("Weigh-in") : t("Water")}
                      meta={format(new Date(entry.timestamp), "h:mm a", { locale })}
                      value={
                        entry.type === "weight"
                          ? `${lbToDisplayWeight(entry.measurement.value, "lb").toFixed(1)} lb`
                          : `${Math.round(mlToOz(entry.measurement.value))} oz`
                      }
                      onClick={() => state.goToMetricDetail(entry.type === "weight" ? "weight" : "water")}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
